import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { CrossIcon } from "../../icons/cross";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/core";

const HeaderContent = { cta: "Discuss a project" };

const Header: React.FC = () => {
  const { cta } = HeaderContent;
  const [value, setValue] = useState<E164Number | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    service: "",
    idea: "",
    budget: "",
    source: "",
    email: "",
    phone: "",
  });

  function handleToggleModal() {
    setShowModal(!showModal);
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      alert("Please enter a valid email.");
      return;
    }

    const emailParams = {
      user_name: formData.name,
      company: formData.company,
      service: formData.service,
      idea: formData.idea,
      budget: formData.budget,
      source: formData.source,
      user_email: formData.email,
      user_phone: formData.phone,
    };
    emailjs
      .send(
        process.env.REACT_APP_EMAIL_SERVICE_KEY || "",
        process.env.REACT_APP_EMAIL_QUERY_TEMPLATE_ID || "",
        emailParams,
        process.env.REACT_APP_EMAIL_PUBLIC_KEY_ID || ""
      )
      .then((response) => {
        setFormData({
          name: "",
          company: "",
          service: "",
          idea: "",
          budget: "",
          source: "",
          email: "",
          phone: "",
        });

        handleToggleModal();
      })
      .catch((error) => {
        alert("Error sending message, please try again.");
        console.error("Email sending error:", error);
      });
  };

  return (
    <div className="w-full absolute z-[2] py-5 md:py-6">
      <div className="w-full mx-auto max-w-[94.25em] px-[1.5em] md:px-[2.5em]">
        <div className="flex justify-between items-center">
          <a className="text-white text-2xl inline-flex font-semibold">
            <span className="text-[#10ba79]">unity</span>Dev
          </a>
          <button
            onClick={handleToggleModal}
            className="w-auto px-[1.42em] py-[0.714em] border border-[#FFFFFF] bg-[#0E0E13] rounded-3xl text-[0.875em] text-white sm:text-[1em] md:text-[1.2em] font-medium transition-all duration-300 hover:bg-white hover:text-[#0E0E13]"
          >
            {cta}
          </button>

          {/* Modal */}
          {showModal && (
            <div className="items-center flex-col justify-center backdrop-blur-[10px] fixed inset-0 z-10 h-screen p-5 flex">
              <div className="flex flex-col py-6 px-5 sm:p-8 rounded-2xl bg-[#162239f2] w-full max-w-[640px] relative overflow-y-auto">
                <span
                  className="w-6 h-6 absolute top-3 right-3 cursor-pointer flex items-center justify-center"
                  onClick={handleToggleModal}
                >
                  <CrossIcon fill="white" className="w-5 h-5" />
                </span>
                <h3 className="text-xl md:text-2xl font-light text-white mb-6">
                  Hello, Team unityDev!
                </h3>

                {/* Form */}
                <form className="w-full" onSubmit={handleSendEmail}>
                  <div className="grid grid-cols-1 gap-4 mb-6">
                    <div className="flex items-center flex-wrap gap-3 md:gap-4">
                      <label className="text-white text-[15px] font-light">
                        My name is
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your Full Name"
                        className="text-white text-[13px] font-normal py-[14px] bg-transparent border-b border-white focus:outline-none h-10 grow"
                      />
                      <label className="text-white text-[15px] font-light">
                        from
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Company name or Website"
                        className="text-white text-[13px] font-normal py-[14px] bg-transparent border-b border-white focus:outline-none h-10 grow"
                      />
                    </div>
                    <div className="flex items-center flex-wrap gap-3 md:gap-4">
                      <label className="text-white text-[15px] font-light">
                        I'd like to discuss
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="text-white text-[13px] font-normal bg-transparent border-b border-white focus:outline-none h-10 grow"
                      >
                        <option>Select Service</option>
                        <option>Design</option>
                        <option>Website Development</option>
                        <option>Graphic Design</option>
                        <option>Brand Activation</option>
                        <option>CMS Development</option>
                      </select>
                    </div>
                    <textarea
                      name="idea"
                      value={formData.idea}
                      onChange={handleChange}
                      placeholder="Pitch your project idea (Optional)"
                      className="text-white text-[13px] font-normal py-[14px] bg-transparent border-b border-white focus:outline-none min-h-10 grow"
                    />
                    <div className="flex flex-wrap gap-3">
                      <label className="text-white text-[15px] font-light">
                        A budget for this project is
                      </label>
                      {[
                        "$2,700",
                        "$2,700 - $5,400",
                        "$5,400 - $10,800",
                        "$10,800+",
                      ].map((budget) => (
                        <button
                          type="button"
                          key={budget}
                          onClick={() => setFormData({ ...formData, budget })}
                          className={`text-xs text-[#a3a3a5] py-[10px] px-[18px] border border-[#a3a3a5] rounded-full hover:text-white hover:border-white ${
                            formData.budget === budget
                              ? "text-white border-white"
                              : ""
                          }`}
                        >
                          {budget}
                        </button>
                      ))}
                    </div>
                    <label className="text-white text-[15px] font-light">
                      I learned about your company through
                    </label>
                    <select
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      className="text-white text-[13px] font-normal bg-transparent border-b border-white focus:outline-none h-10 grow"
                    >
                      <option>Select Source</option>
                      <option>Google Search</option>
                      <option>LinkedIn</option>
                      <option>Dribbble</option>
                      <option>Behance</option>
                      <option>Twitter/X</option>
                      <option>Referral</option>
                    </select>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your Email"
                      className="text-white text-[13px] font-normal py-[14px] bg-transparent border-b border-white focus:outline-none h-10 grow"
                    />
                    <div className="text-white text-[13px] font-normal py-[14px] bg-transparent border-b border-white focus-within:outline-none h-10 grow flex items-center">
                      <PhoneInput
                        placeholder="Enter phone number"
                        value={value}
                        onChange={(val) => {
                          setValue(val);
                          setFormData({ ...formData, phone: val || "" });
                        }}
                        className="!bg-transparent !text-white !text-[13px] !font-normal !w-full [&>input]:bg-transparent [&>input]:text-white [&>input]:text-[13px] [&>input]:font-normal [&>input]:outline-none"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="bg-[#10ba79] text-white py-3 px-6 rounded-xl hover:bg-white hover:text-[#10ba79]"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
